<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime, Duration } from 'luxon';

import {
  ONE_MIN_INTERVAL,
  useElapsedTimeTicker
} from '@satellite/composables/useElapsedTimeTicker';
import PresenterValueFallback from '@satellite/components/elements/presenters/PresenterValueFallback.vue';

import type { DurationUnit } from 'luxon';

type ElapsedTimeVariant = 'formatted' | 'human';

const DEFAULT_UNITS: DurationUnit[] = ['days', 'hours', 'minutes'];

function formatElapsedTime(diff: Duration, format: string): string {
  return diff.toFormat(format);
}

function humanizeElapsedTime(
  diff: Duration,
  units: DurationUnit[] = DEFAULT_UNITS,
  locale?: string
): string {
  const normalized = diff.shiftTo(...units);

  const durationObject: Record<string, number> = {};

  for (const unit of units) {
    const value = Math.floor(normalized.get(unit) ?? 0);

    if (value > 0) {
      durationObject[unit] = value;
    }
  }

  // Luxon's default locale is pinned to en-US at app bootstrap, so `toHuman` needs the active
  // locale passed in or it renders "4 months and 18 days" in every language.
  const truncatedDuration = Duration.fromObject(durationObject, locale ? { locale } : {});

  return truncatedDuration.toHuman({ listStyle: 'long' });
}

function calculateElapsedTime(
  from: string,
  timezone: string | undefined,
  {
    now,
    format,
    variant,
    until,
    units,
    locale
  }: {
    format: string;
    variant: ElapsedTimeVariant;
    until: string | null | undefined;
    units: DurationUnit[];
    now: DateTime;
    locale?: string;
  }
): string | null {
  if (!from) {
    return null;
  }

  const start = DateTime.fromISO(from, { zone: timezone });

  if (!start.isValid) {
    return null;
  }

  const end = until ? DateTime.fromISO(until, { zone: timezone }) : now.setZone(timezone);

  if (!end.isValid) {
    return null;
  }

  const diff = end.diff(start, units, {
    conversionAccuracy: 'longterm'
  });

  if (variant === 'formatted') {
    return formatElapsedTime(diff, format);
  }

  return humanizeElapsedTime(diff, units, locale);
}

const {
  from,
  until = null,
  timezone,
  variant = 'formatted',
  format = 'hh:mm',
  units,
  updateInterval = ONE_MIN_INTERVAL,
  showEmptyState = true
} = defineProps<{
  /**
   * The ISO date string to calculate elapsed time from.
   */
  from: string;

  /**
   * The ISO date string to calculate elapsed time until.
   * If not provided, elapsed time is calculated against the current time.
   */
  until?: string | null;

  timezone?: string;

  variant?: ElapsedTimeVariant;

  format?: string;

  units?: DurationUnit[];

  /**
   * Update frequency in milliseconds.
   *
   * Common values:
   * - 1000 = 1 second
   * - 10000 = 10 seconds
   * - 30000 = 30 seconds
   * - 60000 = 1 minute
   * - 300000 = 5 minutes
   *
   * You can use the constants from @satellite/composables/useElapsedTimeTicker.
   * - ONE_SECOND_INTERVAL = 1000
   * - ONE_MIN_INTERVAL = 60000
   * - FIVE_MIN_INTERVAL = 300000
   * - ONE_HOUR_INTERVAL = 3600000
   *
   * If set to 0, the value is calculated once and does not auto-update.
   */
  updateInterval?: number;

  /**
   * Whether to show empty state text when no date is provided.
   */
  showEmptyState?: boolean;
}>();

const shouldTick = computed(() => {
  return !until && updateInterval > 0;
});

const { now } = useElapsedTimeTicker(
  () => updateInterval,
  () => shouldTick.value
);

const { locale } = useI18n();

const elapsedTime = computed(() => {
  if (!from) {
    return null;
  }

  try {
    return calculateElapsedTime(from, timezone, {
      format,
      variant,
      until,
      units: units ?? [...DEFAULT_UNITS],
      now: now.value,
      locale: locale.value
    });
  } catch (error) {
    console.warn('ElapsedTime: Error calculating elapsed time:', error);

    return null;
  }
});
</script>

<template>
  <span v-bind="$attrs" class="elapsed-time">
    <slot v-if="elapsedTime" name="default">
      {{ elapsedTime }}
    </slot>

    <slot v-else-if="showEmptyState" name="fallback">
      <presenter-value-fallback />
    </slot>
  </span>
</template>

<style scoped lang="scss">
.elapsed-time {
  // This avoids layout shift as the time progresses.
  min-inline-size: 72px;
}
</style>
